/**
 * Вход через аккаунт Intch — ЕДИНСТВЕННОЕ место приложения, где `fetch` идёт мимо `http.ts`.
 *
 * Почему мимо: код подтверждения монолит держит в своей Django-сессии, и оба шага (выслать код,
 * обменять код на токены) обязаны прийти из ОДНОГО браузера с его cookie `sessionid`. Через наш
 * сервер это не работает — проверено: вход из другой сессии отвечает 401. Поэтому браузер зовёт
 * монолит напрямую, с `credentials: 'include'`.
 *
 * Токены хранятся так же, как их хранит intch.org (`web/src/api/auth.ts`): cookie `access` и
 * `refresh` на `.intch.org`, НЕ HttpOnly — их читает и перезаписывает JS обоих сайтов. Так вход
 * общий в обе стороны: вошёл на intch.org — вошёл здесь, и наоборот. Имена и параметры cookie
 * скопированы оттуда и расходиться с ними не могут.
 *
 * Без `VITE_INTCH_AUTH_BASE` в dev-сборке вход подменяется тестовым: код любой из шести цифр, токен
 * `mock.<id>.<email>`, который сервер принимает только в `MODE=mock` без адреса монолита. В боевой
 * сборке без адреса входа нет вовсе — fail closed, как на сервере.
 */

const BASE = String(import.meta.env?.VITE_INTCH_AUTH_BASE ?? '').replace(/\/+$/, '')
const COOKIE_DOMAIN = String(import.meta.env?.VITE_COOKIE_DOMAIN ?? '').trim()

/** Имена cookie — те же, что у intch.org. Переименовать здесь значит разорвать общий вход. */
const ACCESS = 'access'
const REFRESH = 'refresh'
/** Срок cookie, как у intch.org: 5 лет. Сам access живёт 20 минут — срок cookie про другое. */
const COOKIE_MAX_AGE = 5 * 365 * 24 * 60 * 60

/** `sales`, не `b2b_sales`: только с ним новый адрес создаётся со сценарием sales, а существующему сценарий не меняется. */
const WEB_SOURCE = 'sales'

export const authMocked = !BASE && Boolean(import.meta.env?.DEV)
export const authConfigured = Boolean(BASE) || authMocked

/* ---------- cookie ---------- */

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=')
    if (key === name) {
      try {
        return decodeURIComponent(rest.join('=')) || null
      } catch {
        return null
      }
    }
  }
  return null
}

const cookieTail = (domain: string | null, maxAge: number) =>
  [
    'path=/',
    `max-age=${maxAge}`,
    'SameSite=Lax',
    ...(domain ? [`domain=${domain}`] : []),
    ...(typeof location !== 'undefined' && location.protocol === 'https:' ? ['Secure'] : []),
  ].join('; ')

/** На localhost домена нет — cookie ставится на текущий хост. */
const domainForHere = () => (COOKIE_DOMAIN && typeof location !== 'undefined' && location.hostname.endsWith(COOKIE_DOMAIN.replace(/^\./, '')) ? COOKIE_DOMAIN : null)

const writeCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${cookieTail(domainForHere(), COOKIE_MAX_AGE)}`
}

/** Стираются и с доменом, и без: так же, как clearCookies() у intch.org, — cookie могли поставить оба способа. */
export const clearTokens = () => {
  if (typeof document === 'undefined') return
  for (const name of [ACCESS, REFRESH]) {
    document.cookie = `${name}=; ${cookieTail(null, 0)}`
    if (COOKIE_DOMAIN) document.cookie = `${name}=; ${cookieTail(COOKIE_DOMAIN, 0)}`
  }
}

export const accessToken = () => readCookie(ACCESS)
const refreshToken = () => readCookie(REFRESH)
export const hasSession = () => Boolean(accessToken() || refreshToken())

/** `exp` из payload access-токена в миллисекундах, или null — у тестового токена и у битого его нет. */
const expiryOf = (token: string): number | null => {
  const part = token.split('.')[1]
  if (!part) return null
  try {
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown }
    const exp = Number(payload.exp)
    return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null
  } catch {
    return null
  }
}

/** Обновлять заранее — за минуту до конца. Так запас есть и у запроса, который долго идёт до сервера. */
const RENEW_BEFORE_MS = 60_000

/**
 * Пора ли обновить пару ДО запроса. Истёкший access сервер не отличает от анонима на маршрутах, которые
 * открыты и анониму (GET /api/runs/:id/rows молча отдаёт строки без контактов), так что ждать 401 там
 * нельзя. Access нет, а refresh есть — тоже пора: cookie access могла истечь раньше refresh.
 */
export const needsRenewal = (now = Date.now()): boolean => {
  if (!refreshToken() || authMocked) return false
  const access = accessToken()
  if (!access) return true
  const expiry = expiryOf(access)
  return expiry != null && expiry - now < RENEW_BEFORE_MS
}

const saveTokens = (access: string, refresh: string | null) => {
  writeCookie(ACCESS, access)
  if (refresh) writeCookie(REFRESH, refresh)
}

/* ---------- ошибки ---------- */

/**
 * Почему вход не удался, словом, которое экран переводит в предложение. Коды — только эти: они
 * становятся ключами переводов, построенными динамически.
 */
export type SignInFailure =
  | 'blocked' // адрес в стоп-листе или одноразовая почта
  | 'throttled' // слишком много попыток
  | 'badCode' // код не подошёл, уже использован или открыт в другом браузере
  | 'integration' // монолит не узнал наш источник — баг интеграции, не человека
  | 'unavailable' // сеть или монолит лёг

export class SignInError extends Error {
  readonly failure: SignInFailure
  /** Через сколько секунд можно попробовать снова, когда монолит это сказал. */
  readonly retryAfter: number | null

  constructor(failure: SignInFailure, retryAfter: number | null = null) {
    super(`sign-in failed: ${failure}`)
    this.name = 'SignInError'
    this.failure = failure
    this.retryAfter = retryAfter
  }
}

/** DRF пишет троттлинг словами: «Expected available in 42 seconds». */
const secondsIn = (body: unknown): number | null => {
  const text = JSON.stringify(body ?? '')
  const match = text.match(/(\d+)\s*second/)
  return match ? Number(match[1]) : null
}

const postJson = async (path: string, body: unknown): Promise<{ status: number; data: unknown }> => {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    throw new SignInError('unavailable')
  }
  let data: unknown = null
  try {
    data = await response.json()
  } catch {
    data = null
  }
  return { status: response.status, data }
}

const throttled = (status: number, data: unknown) =>
  status === 429 || /throttl/i.test(JSON.stringify(data ?? ''))

/* ---------- mock ---------- */

const MOCK_EMAIL_KEY = 'intch.auth.mock.email'

const mockId = (email: string) => {
  let hash = 0
  for (const char of email.toLowerCase()) hash = (hash * 31 + char.charCodeAt(0)) >>> 0
  return hash.toString(36)
}

const base64url = (text: string) =>
  btoa(String.fromCharCode(...new TextEncoder().encode(text))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')

/* ---------- шаги входа ---------- */

export type SubmitEmailResult = { created: boolean; cooldownSeconds: number }

/** Шаг 1: выслать код. Монолит заводит аккаунт, если адреса ещё нет. */
export const submitEmail = async (email: string): Promise<SubmitEmailResult> => {
  if (authMocked) {
    sessionStorage.setItem(MOCK_EMAIL_KEY, email.trim())
    return { created: false, cooldownSeconds: 60 }
  }
  if (!BASE) throw new SignInError('integration')

  const { status, data } = await postJson('/auth/email/submit/', { email: email.trim(), web_source: WEB_SOURCE })
  if (status >= 200 && status < 300) {
    const body = (data ?? {}) as { created?: boolean; cooldown_seconds?: number }
    return { created: Boolean(body.created), cooldownSeconds: Number(body.cooldown_seconds) || 60 }
  }
  if (throttled(status, data)) throw new SignInError('throttled', secondsIn(data))
  if (status === 403) throw new SignInError('blocked')
  if (status === 404) throw new SignInError('integration')
  if (status >= 500) throw new SignInError('unavailable')
  throw new SignInError('blocked')
}

/** Шаг 2: обменять код на токены. Код одноразовый; неверный или из другого браузера — 401. */
export const submitCode = async (code: string): Promise<{ firstLogin: boolean }> => {
  const clean = code.replace(/\D/g, '')
  if (authMocked) {
    const email = sessionStorage.getItem(MOCK_EMAIL_KEY) ?? ''
    if (clean.length !== 6 || !email) throw new SignInError('badCode')
    saveTokens(`mock.${mockId(email)}.${base64url(email)}`, null)
    return { firstLogin: false }
  }
  if (!BASE) throw new SignInError('integration')

  const { status, data } = await postJson('/auth/email/login/', { code: clean })
  const body = (data ?? {}) as { access?: string; refresh?: string; first_login?: boolean }
  if (status >= 200 && status < 300 && body.access) {
    saveTokens(body.access, body.refresh ?? null)
    return { firstLogin: Boolean(body.first_login) }
  }
  if (throttled(status, data)) throw new SignInError('throttled', secondsIn(data))
  if (status === 401 || status === 400) throw new SignInError('badCode')
  throw new SignInError('unavailable')
}

/**
 * Чем кончилось обновление. `rejected` — монолит сказал, что ключ не годится: вход закончился, cookie
 * стираются. `unavailable` — сеть или монолит молчат: вход НЕ закончился, и выходить из аккаунта (а с
 * ним и с intch.org, cookie общие) из-за минутного сбоя — худший ответ из возможных.
 */
export type RefreshOutcome = 'ok' | 'rejected' | 'unavailable'

/** Обновить пару по refresh — access живёт 20 минут. Обе cookie перезаписываются (ротация). */
export const refreshTokens = async (): Promise<RefreshOutcome> => {
  const refresh = refreshToken()
  if (!refresh || authMocked || !BASE) return 'rejected'
  let answer: { status: number; data: unknown }
  try {
    answer = await postJson('/auth/token/refresh/', { refresh })
  } catch {
    return 'unavailable'
  }
  const body = (answer.data ?? {}) as { access?: string; refresh?: string }
  if (answer.status >= 200 && answer.status < 300 && body.access) {
    saveTokens(body.access, body.refresh ?? refresh)
    return 'ok'
  }
  /* Замок refresh в api/http.ts — на одну вкладку. Соседняя вкладка или intch.org могли обновить ту
     же пару первыми, и ротация сделала наш ключ недействительным. Cookie тогда уже другая — это не
     конец входа, а чужой успешный refresh. */
  if (answer.status === 400 || answer.status === 401) return refreshToken() !== refresh ? 'ok' : 'rejected'
  return 'unavailable'
}

/**
 * Выйти — это стереть cookie. `POST /auth/logout/` зовётся из вежливости: на платформе он ключ
 * обновления не отзывает, так что настоящий выход — именно стирание.
 */
export const signOutOfIntch = async () => {
  const refresh = refreshToken()
  clearTokens()
  if (!BASE || authMocked || !refresh) return
  try {
    await fetch(`${BASE}/auth/logout/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    })
  } catch {
    /* cookie уже стёрты — этого достаточно */
  }
}
